import { useAtom } from 'jotai';
import React, { useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { FocusHandler, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { focusKeyPrevAtom } from '@smart-tv/atoms';
import ContentItem, {
  Props as ContentItemProps
} from '@smart-tv/components/content-item/content-item';
import { StreamOptions } from '@smart-tv/components/player/vod-wrapper/_contexts/stream-player-context';
import useVodPlayer from '@smart-tv/components/player/vod-wrapper/_hooks/use-vod-player';
import {
  ContentItem as IContentItem,
  ContentType,
  StreamType,
  UIElement
} from '@smart-tv/typings/content';
import isFutureDate from '@smart-tv/utils/date/is-future-date';
import addQueryParams from '@smart-tv/utils/routing/add-query-params';
import encodeProgramSheetParams from '@smart-tv/utils/routing/encode-program-sheet-params';
import { isVodContent } from './utils';
import ContentSliderItemUnavailableDialog from './content-slider-item-unavailable-dialog';
import styles from './content-slider.module.scss';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';

export interface ContentSliderItemProps extends ContentItemProps {
  onFocus?: FocusHandler;
  onMouseEnter?: () => void;
  collections?: boolean;
  arrowPressHandler?: (direction: string) => boolean;
  isFocused?: boolean;
  isUnavailable?: boolean;
  onRendered?: () => void;
  onLiveTVContentRendered?: () => void;
  allStripeElementsRendered?: boolean;
  focusKey: string;
  stripePrimaryAction?: UIElement['primaryAction'];
  handleFavoritesUpdate?: (item: IContentItem, action: 'add' | 'remove') => void;
  setReadyForFocus?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ContentSliderItem: React.FC<ContentSliderItemProps> = (
  props: ContentSliderItemProps
) => {
  const {
    isFocused,
    contentItem,
    contentSliderIndex,
    onFocus,
    collections,
    arrowPressHandler,
    setAnimating,
    focusKey,
    stripePrimaryAction,
    isUnavailable,
    handleFavoritesUpdate,
    className,
    onMouseEnter,
    setReadyForFocus
  } = props;

  const [focusKeyPrev, setFocusKeyPrev] = useAtom(focusKeyPrevAtom);

  const universeId = stripePrimaryAction?.actionParameters.find(
    (actionParameter) => actionParameter.parameterName === 'UNIVERSE_ID'
  )?.parameterValue;

  const subProviderId = stripePrimaryAction?.actionParameters.find(
    (actionParameter) => actionParameter.parameterName === 'SUB_PROVIDER_ID'
  )?.parameterValue;

  const provider = stripePrimaryAction?.actionParameters.find(
    (actionParameter) => actionParameter.parameterName === 'PROVIDERS'
  )?.parameterValue;

  const universeIdString = universeId ? String(universeId) : undefined;
  const subProviderIdString = subProviderId ? String(subProviderId) : undefined;
  const providerString = provider ? String(provider) : undefined;

  const navigate = useNavigate();
  const { handleOpen } = useVodPlayer();

  const [showUnavailableDialog, setShowUnavailableDialog] = React.useState(false);

  const handleEnterPress = React.useCallback(async () => {
    setAnimating(true);

    /*
      Toggle here to show the unavailable dialog if the content is marked as unavailable. 
      This is based on the assumption that if content is unavailable, it should not be playable 
      and thus we should inform the user with a dialog. If this is not the desired behavior, 
      we can remove this check and allow the user to attempt to play the content, which may 
      then show an error dialog if it fails to play. 
      - MRA
    */
    if (isUnavailable) {
      setAnimating(false);
      setShowUnavailableDialog(true);
      return;
    }
    // END

    if (contentItem) {
      if (contentItem.startTime && !isFutureDate(contentItem.startTime) && contentItem.sid) {
        setFocusKeyPrev({ focusKeyPrev: focusKey, isLiveTV: true });

        const streamOptions: StreamOptions = {
          streamType: StreamType.LiveStream,
          streamParams: { sid: contentItem.sid }
        };
        handleOpen(streamOptions);
      } else if (isVodContent(contentItem.contentType)) {
        const { contentId } = contentItem;

        if (contentId) {
          setFocusKeyPrev({ focusKeyPrev: focusKey, isLiveTV: false });

          navigate(
            addQueryParams({
              programsheet: encodeProgramSheetParams(
                undefined,
                undefined,
                contentId,
                contentItem.contentType,
                subProviderIdString,
                universeIdString,
                providerString
              )
            })
          );
        }
      } else if (contentItem.sid && contentItem.eventId) {
        setFocusKeyPrev({ focusKeyPrev: focusKey, isLiveTV: false });

        navigate(
          addQueryParams({
            programsheet: encodeProgramSheetParams(
              contentItem.sid,
              contentItem.eventId,
              undefined,
              undefined,
              subProviderIdString,
              universeIdString,
              providerString
            )
          })
        );
      } else if (contentItem.contentType === ContentType.Provider) {
        navigate(
          `provideruniverse/${contentItem.providerCollectionName}/${contentItem.providerCollectionId}`
        );
        //this is needed to ensure we start at the top of the provider universe page when navigating to it without the focus system
        IS_BROWSER && scrollTo({ top: 0, behavior: 'instant' });
      }
    }
    const animationTimer = setTimeout(() => {
      setAnimating(false);
    }, 500);
    return () => {
      clearTimeout(animationTimer);
    };
  }, [
    contentItem,
    focusKey,
    handleOpen,
    navigate,
    setAnimating,
    setFocusKeyPrev,
    subProviderIdString,
    universeIdString,
    providerString,
    isUnavailable
  ]);

  const { ref, focused, focusSelf } = useFocusable({
    onFocus,
    onEnterPress: handleEnterPress,
    onArrowPress: arrowPressHandler,
    focusKey: focusKey
  });

  useLayoutEffect(() => {
    if (isFocused) {
      focusSelf();
      setFocusKeyPrev(null);
    }
  }, [focusSelf, isFocused, setFocusKeyPrev]);

  // Check on mount if we should focus the first item in collections
  const shouldFocusFirstItemOnMount = React.useMemo(() => {
    if (!collections || contentSliderIndex !== 0) return false;
    const hasCollectionFocusToRestore =
      focusKeyPrev?.focusKeyPrev && focusKeyPrev.focusKeyPrev.endsWith('-collection');
    return !hasCollectionFocusToRestore;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only calculate once on mount

  useEffect(() => {
    if (shouldFocusFirstItemOnMount) {
      focusSelf();
    }
  }, [shouldFocusFirstItemOnMount, focusSelf]);

  // Set ready for focus on mount to ensure that the parent knows this item is ready for focus. "setReadyForFocus" is only defined for a single item in a single stripe, so we only call it for the last item in the stripe.
  React.useEffect(() => {
    if (setReadyForFocus) {
      setReadyForFocus(true);
    }
  }, []);

  return (
    <>
      <ContentSliderItemUnavailableDialog
        contentItem={contentItem}
        showUnavailableDialog={showUnavailableDialog}
        setShowUnavailableDialog={setShowUnavailableDialog}
        focusKey={focusKey}
        handleFavoritesUpdate={handleFavoritesUpdate}
      />
      <ContentItem
        ref={ref}
        focused={focused}
        onClick={handleEnterPress}
        onMouseEnter={onMouseEnter}
        {...props}
        className={[styles['content-slider-item'], className].filter(Boolean).join(' ')}
      />
    </>
  );
};
export default React.memo(ContentSliderItem);
